import { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useReadContract, useBalance } from 'wagmi';
import { parseUnits, formatUnits } from 'viem';
import { CONTRACT_ADDRESSES, ROUTER_ABI, EMBER_TOKEN_ABI, WFLR_ABI } from '../config/contracts';
import { BACKEND_URL } from '../config/api';
import { TOKEN_LOGOS } from '../config/tokens';

const DEADLINE_MINUTES = 20;
const EXPLORER = 'https://flare-explorer.flare.network';

function SwapSection({ isConnected }) {
  const { address } = useAccount();
  const [tokens, setTokens] = useState([]);
  const [fromToken, setFromToken] = useState(null);
  const [toToken, setToToken] = useState(null);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [slippage, setSlippage] = useState('0.5');
  const [status, setStatus] = useState('');

  const { writeContract, data: txHash, isPending, error: writeError } = useWriteContract();
  const { isLoading: isConfirming, isSuccess, isError: isReceiptError, error: receiptError } = useWaitForTransactionReceipt({
    hash: txHash,
    timeout: 120_000, // 2 minutes – prevents UI locking up on slow/unreliable RPC
  });

  // Separate hook for approval transactions
  const { writeContract: writeApproval, data: approvalTxHash, isPending: isApprovePending, error: approveError } = useWriteContract();
  const { isLoading: isApprovalConfirming, isSuccess: isApprovalSuccess } = useWaitForTransactionReceipt({
    hash: approvalTxHash,
    timeout: 120_000,
  });

  const notDeployed = !CONTRACT_ADDRESSES.ROUTER;

  // Resolve the path address for a token — FLR routes through WFLR internally
  const pathAddr = (token) => token?.symbol === 'FLR' ? CONTRACT_ADDRESSES.WFLR : token?.flare_contract;

  // Parse fromAmount safely for getAmountsOut
  let amountInParsed = null;
  try {
    if (fromAmount && fromToken) amountInParsed = parseUnits(fromAmount, fromToken.decimals ?? 18);
  } catch {}

  const fromPath = pathAddr(fromToken);
  const toPath = pathAddr(toToken);

  // Check if this is a wrap/unwrap operation
  const isWrapUnwrap = (fromToken?.symbol === 'FLR' && toToken?.symbol === 'WFLR') ||
                       (fromToken?.symbol === 'WFLR' && toToken?.symbol === 'FLR');

  const canEstimate = !!amountInParsed && !!fromPath && !!toPath && fromPath !== toPath && !notDeployed && !isWrapUnwrap;

  const { data: amountsOut, error: amountsOutError, isLoading: isLoadingAmounts } = useReadContract({
    address: CONTRACT_ADDRESSES.ROUTER,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: canEstimate ? [amountInParsed, [fromPath, toPath]] : undefined,
    query: {
      enabled: canEstimate && !!CONTRACT_ADDRESSES.ROUTER,
      retry: false,
      throwOnError: false,
    },
  });

  // Get native FLR balance
  const { data: flrBalance } = useBalance({
    address: address,
    query: {
      enabled: !!address,
    },
  });

  // Get fromToken balance (for ERC20 tokens)
  const { data: fromTokenBalance } = useReadContract({
    address: fromToken?.flare_contract,
    abi: EMBER_TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!(fromToken?.flare_contract && address),
      retry: false,
      throwOnError: false,
    },
  });

  // Get toToken balance (for ERC20 tokens)
  const { data: toTokenBalance } = useReadContract({
    address: toToken?.flare_contract,
    abi: EMBER_TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!(toToken?.flare_contract && address),
      retry: false,
      throwOnError: false,
    },
  });

  // Check token allowance for non-FLR tokens
  const { data: tokenAllowance } = useReadContract({
    address: fromToken?.flare_contract,
    abi: EMBER_TOKEN_ABI,
    functionName: 'allowance',
    args: address && CONTRACT_ADDRESSES.ROUTER ? [address, CONTRACT_ADDRESSES.ROUTER] : undefined,
    query: {
      enabled: !!(fromToken?.flare_contract && address && CONTRACT_ADDRESSES.ROUTER && fromToken?.symbol !== 'FLR'),
      retry: false,
      throwOnError: false,
    },
  });

  // Calculate display balances
  const getTokenBalance = (token) => {
    if (!isConnected || !token) return '0.0000';

    // Use token decimals, but default to 18 if not set or if 0 (which is likely incorrect for ERC20 tokens)
    const tokenDecimals = (token.decimals && token.decimals > 0) ? token.decimals : 18;
    const decimalPlaces = Math.min(tokenDecimals, 4);
    const defaultValue = '0.' + '0'.repeat(decimalPlaces);

    if (token.symbol === 'FLR') {
      return flrBalance ? parseFloat(formatUnits(flrBalance.value, 18)).toFixed(4) : '0.0000';
    } else if (token.symbol === fromToken?.symbol && fromTokenBalance) {
      return parseFloat(formatUnits(fromTokenBalance, tokenDecimals)).toFixed(decimalPlaces);
    } else if (token.symbol === toToken?.symbol && toTokenBalance) {
      return parseFloat(formatUnits(toTokenBalance, tokenDecimals)).toFixed(decimalPlaces);
    }
    return defaultValue;
  };

  const fromBalance = getTokenBalance(fromToken);
  const toBalance = getTokenBalance(toToken);

  useEffect(() => {
    // Handle wrap/unwrap 1:1 conversion
    if (fromAmount && fromToken && toToken && isWrapUnwrap) {
      console.log('[WRAP/UNWRAP] 1:1 conversion:', {
        fromAmount,
        fromToken: fromToken.symbol,
        toToken: toToken.symbol
      });
      setToAmount(fromAmount);
      return;
    }

    if (amountsOut && amountsOut.length >= 2 && toToken) {
      const out = amountsOut[amountsOut.length - 1];
      // Use token decimals, but default to 18 if not set or if 0 (which is likely incorrect for ERC20 tokens)
      const tokenDecimals = (toToken.decimals && toToken.decimals > 0) ? toToken.decimals : 18;
      const decimalPlaces = Math.min(tokenDecimals, 6);
      const estimatedAmount = parseFloat(formatUnits(out, tokenDecimals)).toFixed(decimalPlaces);
      console.log('[SWAP] Price estimation successful:', {
        amountsOut: amountsOut.map(a => a.toString()),
        estimatedOutput: estimatedAmount,
        toToken: toToken.symbol,
        decimals: tokenDecimals
      });
      setToAmount(estimatedAmount);
    } else {
      setToAmount('');
    }
  }, [amountsOut, toToken, fromAmount, fromToken, isWrapUnwrap]);

  useEffect(() => {
    if (amountsOutError) {
      console.error('[SWAP] Failed to estimate swap amounts:', amountsOutError);
      console.error('[SWAP] This might indicate:', {
        possibleCauses: [
          'No liquidity pool exists for this pair',
          'Insufficient liquidity in the pool',
          'Invalid token addresses',
          'Router contract issue'
        ],
        fromToken: fromToken?.symbol,
        toToken: toToken?.symbol,
        fromPath,
        toPath,
        error: amountsOutError?.message
      });
    }
  }, [amountsOutError, fromToken, toToken, fromPath, toPath]);

  useEffect(() => {
    if (!fromAmount) setToAmount('');
  }, [fromAmount]);

  useEffect(() => {
    console.log('[SWAP] Component mounted');
    console.log('[SWAP] Contract addresses:', CONTRACT_ADDRESSES);
    loadTokens();
  }, []);

  useEffect(() => {
    if (txHash) {
      console.log('[SWAP] Transaction hash received:', txHash);
      console.log('[SWAP] View on block explorer: https://flare-explorer.flare.network/tx/' + txHash);
    }
  }, [txHash]);

  useEffect(() => {
    console.log('[SWAP] Transaction states:', { isPending, isConfirming, isSuccess });
    if (isPending) {
      console.log('[SWAP] Waiting for user to confirm in wallet...');
    }
    if (isConfirming) {
      console.log('[SWAP] Transaction submitted, waiting for confirmation...');
    }
  }, [isPending, isConfirming, isSuccess]);

  useEffect(() => {
    if (isSuccess) {
      console.log('[SWAP] Transaction confirmed successfully!');
      const isWrapOp = (fromToken?.symbol === 'FLR' && toToken?.symbol === 'WFLR') ||
                       (fromToken?.symbol === 'WFLR' && toToken?.symbol === 'FLR');
      let message = 'Swap confirmed!';
      if (isWrapOp) {
        message = fromToken?.symbol === 'FLR' ? 'Wrap confirmed!' : 'Unwrap confirmed!';
      }
      setStatus(message);
      setFromAmount('');
      setToAmount('');
    }
  }, [isSuccess, fromToken, toToken]);

  useEffect(() => {
    if (writeError) {
      console.error('[SWAP] Write contract error:', writeError);
      console.error('[SWAP] Error details:', {
        name: writeError?.name,
        message: writeError?.message,
        shortMessage: writeError?.shortMessage,
        cause: writeError?.cause,
        code: writeError?.code,
        details: writeError?.details,
        metaMessages: writeError?.metaMessages
      });
      setStatus(`Error: ${writeError?.shortMessage || writeError?.message || 'Transaction failed'}`);
    }
  }, [writeError]);

  useEffect(() => {
    if (receiptError) {
      console.error('[SWAP] Transaction receipt error:', receiptError);
      console.error('[SWAP] Receipt error details:', {
        name: receiptError?.name,
        message: receiptError?.message,
        shortMessage: receiptError?.shortMessage
      });
      setStatus(`Transaction failed: ${receiptError?.shortMessage || receiptError?.message || 'Unknown error'}`);
    }
  }, [receiptError]);

  useEffect(() => {
    if (isReceiptError && txHash) {
      setStatus(`⚠️ Could not confirm receipt — check explorer:\n${EXPLORER}/tx/${txHash}`);
    }
  }, [isReceiptError, txHash]);

  useEffect(() => {
    if (approvalTxHash) {
      console.log('[APPROVAL] Transaction hash received:', approvalTxHash);
    }
  }, [approvalTxHash]);

  useEffect(() => {
    if (isApprovalSuccess) {
      console.log('[APPROVAL] Approval confirmed successfully!');
      setStatus('Token approved! You can now swap.');
    }
  }, [isApprovalSuccess]);

  useEffect(() => {
    if (approveError) {
      console.error('[APPROVAL] Approval error:', approveError);
      setStatus(`Approval failed: ${approveError?.shortMessage || approveError?.message || 'Unknown error'}`);
    }
  }, [approveError]);

  const loadTokens = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tokens/list?status=approved`);
      const data = await res.json();

      // Add "w" prefix to bridged token names for clarity
      // IMPORTANT: All wrapped tokens on Flare have 18 decimals (contract standard),
      // even though database stores XRPL native decimals for bridge scaling
      const wrappedTokens = (data.tokens || [])
        .filter(t => t.flare_contract)
        .map(t => ({
          ...t,
          symbol: `w${t.symbol}`,
          name: `Wrapped ${t.name}`,
          decimals: 18, // Override: all wrapped ERC20 tokens have 18 decimals on Flare
        }));

      const list = [
        { symbol: 'FLR', name: 'Flare (native)', flare_contract: null, decimals: 18 },
        { symbol: 'WFLR', name: 'Wrapped Flare', flare_contract: CONTRACT_ADDRESSES.WFLR, decimals: 18 },
        { symbol: 'EMBER', name: 'EmberX Token', flare_contract: CONTRACT_ADDRESSES.EMBER_TOKEN, decimals: 18 },
        ...wrappedTokens,
      ];
      setTokens(list);
      if (list.length >= 2) { setFromToken(list[0]); setToToken(list[2]); }
    } catch (e) { console.error(e); }
  };

  const handleApprove = async () => {
    if (!fromToken || !fromAmount || !isConnected) {
      console.log('[APPROVAL] Validation failed:', { fromToken, fromAmount, isConnected });
      return;
    }

    const amountIn = parseUnits(fromAmount, fromToken.decimals ?? 18);
    console.log('[APPROVAL] Initiating token approval:', {
      token: fromToken.symbol,
      tokenAddress: fromToken.flare_contract,
      spender: CONTRACT_ADDRESSES.ROUTER,
      amount: amountIn.toString()
    });

    try {
      writeApproval({
        address: fromToken.flare_contract,
        abi: EMBER_TOKEN_ABI,
        functionName: 'approve',
        args: [CONTRACT_ADDRESSES.ROUTER, amountIn],
      });
      setStatus('Approving token spending...');
    } catch (e) {
      console.error('[APPROVAL] Error during approval:', e);
      setStatus('Approval failed. Please try again.');
    }
  };

  const handleSwap = async () => {
    if (!fromToken || !toToken || !fromAmount || notDeployed || !isConnected) {
      console.log('[SWAP] Validation failed:', { fromToken, toToken, fromAmount, notDeployed, isConnected });
      return;
    }

    // Check if this is a wrap/unwrap operation (FLR ↔ WFLR)
    const isWrapOperation = (fromToken.symbol === 'FLR' && toToken.symbol === 'WFLR') ||
                           (fromToken.symbol === 'WFLR' && toToken.symbol === 'FLR');

    if (isWrapOperation) {
      // Handle wrap/unwrap directly through WFLR contract
      const amountIn = parseUnits(fromAmount, fromToken.decimals ?? 18);

      try {
        if (fromToken.symbol === 'FLR') {
          // Wrap: FLR → WFLR (deposit)
          console.log('[WRAP] Wrapping FLR to WFLR:', {
            amount: fromAmount,
            amountIn: amountIn.toString(),
            wflrContract: CONTRACT_ADDRESSES.WFLR
          });
          writeContract({
            address: CONTRACT_ADDRESSES.WFLR,
            abi: WFLR_ABI,
            functionName: 'deposit',
            value: amountIn,
          });
          setStatus('Wrapping FLR to WFLR...');
        } else {
          // Unwrap: WFLR → FLR (withdraw)
          console.log('[UNWRAP] Unwrapping WFLR to FLR:', {
            amount: fromAmount,
            amountIn: amountIn.toString(),
            wflrContract: CONTRACT_ADDRESSES.WFLR
          });
          writeContract({
            address: CONTRACT_ADDRESSES.WFLR,
            abi: WFLR_ABI,
            functionName: 'withdraw',
            args: [amountIn],
          });
          setStatus('Unwrapping WFLR to FLR...');
        }
      } catch (e) {
        console.error('[WRAP/UNWRAP] Error:', e);
        setStatus('Wrap/Unwrap failed. Please try again.');
      }
      return;
    }

    // Regular swap operation
    const deadline = Math.floor(Date.now() / 1000) + DEADLINE_MINUTES * 60;
    const amountIn = parseUnits(fromAmount, fromToken.decimals ?? 18);
    const path = [fromPath, toPath];

    // Apply slippage to estimated output
    const slippageFactor = Math.round(parseFloat(slippage) * 10); // 0.5% -> 5 (of 1000)
    const amountOutMin = amountsOut && amountsOut.length >= 2
      ? amountsOut[amountsOut.length - 1] * BigInt(1000 - slippageFactor) / 1000n
      : 0n;

    console.log('[SWAP] Initiating swap:', {
      fromToken: fromToken.symbol,
      toToken: toToken.symbol,
      fromAmount,
      amountIn: amountIn.toString(),
      amountOutMin: amountOutMin.toString(),
      path,
      deadline,
      router: CONTRACT_ADDRESSES.ROUTER,
      userAddress: address,
      slippage: slippage + '%'
    });

    // Check token allowance for ERC-20 tokens (just log it, UI now handles approval)
    if (fromToken.symbol !== 'FLR') {
      const allowance = tokenAllowance || 0n;
      console.log('[SWAP] Token allowance check:', {
        token: fromToken.symbol,
        currentAllowance: allowance.toString(),
        requiredAmount: amountIn.toString(),
        hasEnoughAllowance: allowance >= amountIn
      });
    }

    try {
      if (fromToken.symbol === 'FLR') {
        // Native FLR -> ERC-20
        console.log('[SWAP] Calling swapExactFLRForTokens', { value: amountIn.toString() });
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'swapExactFLRForTokens',
          args: [amountOutMin, path, address, deadline],
          value: amountIn,
        });
      } else if (toToken.symbol === 'FLR') {
        // ERC-20 -> Native FLR
        console.log('[SWAP] Calling swapExactTokensForFLR');
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'swapExactTokensForFLR',
          args: [amountIn, amountOutMin, path, address, deadline],
        });
      } else {
        // ERC-20 -> ERC-20
        console.log('[SWAP] Calling swapExactTokensForTokens');
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'swapExactTokensForTokens',
          args: [amountIn, amountOutMin, path, address, deadline],
        });
      }
      setStatus('Waiting for confirmation...');
    } catch (e) {
      console.error('[SWAP] Error during swap:', e);
      console.error('[SWAP] Error details:', {
        name: e?.name,
        message: e?.message,
        code: e?.code,
        data: e?.data,
        stack: e?.stack
      });
      setStatus('Swap failed. Check your balance and try again.');
    }
  };

  const flipTokens = () => {
    const tmp = fromToken; setFromToken(toToken); setToToken(tmp);
    setFromAmount(''); setToAmount('');
  };

  const inputStyle = { flex: 1, minWidth: 0, padding: '12px', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', color: '#fff', fontSize: '1rem' };
  const selectStyle = { flexShrink: 0, width: '110px', padding: '12px 8px', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', cursor: 'pointer' };

  return (
    <div className="content-section active">
      <div className="section">
        <div className="section-title">Swap Tokens</div>

        {notDeployed && (
          <div style={{ backgroundColor: '#0f0520', border: '1px solid rgba(109, 40, 217, 0.5)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#C4B5FD', fontSize: '0.875rem', lineHeight: '1.6' }}>
            Contracts not yet deployed. Run from your local machine: <code>cd contracts && npm install && npx hardhat run scripts/deploy.js --network flare</code>
          </div>
        )}

        <div style={{ marginBottom: '8px' }}>
          <label style={{ color: '#9ca3af', fontSize: '0.875rem' }}>From</label>
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', width: '100%', overflow: 'hidden', alignItems: 'center' }}>
            <input style={inputStyle} type="number" placeholder="0.0" value={fromAmount} onChange={e => setFromAmount(e.target.value)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {fromToken && TOKEN_LOGOS[fromToken.symbol] && (
                <img
                  src={TOKEN_LOGOS[fromToken.symbol]}
                  alt={fromToken.symbol}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #3a3a3a'
                  }}
                />
              )}
              <select style={selectStyle} value={fromToken?.symbol || ''} onChange={e => setFromToken(tokens.find(t => t.symbol === e.target.value))}>
                {tokens.map(t => <option key={t.symbol} value={t.symbol}>{t.symbol}</option>)}
              </select>
            </div>
          </div>
          {isConnected && fromToken && (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px', textAlign: 'right' }}>
              Available: {fromBalance} {fromToken.symbol}
            </div>
          )}
        </div>

        <div onClick={flipTokens} style={{ textAlign: 'center', fontSize: '20px', color: '#A78BFA', cursor: 'pointer', margin: '8px 0' }}>⇅</div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ color: '#9ca3af', fontSize: '0.875rem' }}>To (estimated)</label>
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', width: '100%', overflow: 'hidden', alignItems: 'center' }}>
            <input style={{ ...inputStyle, color: '#9ca3af' }} type="number" placeholder="0.0" value={toAmount} readOnly />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {toToken && TOKEN_LOGOS[toToken.symbol] && (
                <img
                  src={TOKEN_LOGOS[toToken.symbol]}
                  alt={toToken.symbol}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #3a3a3a'
                  }}
                />
              )}
              <select style={selectStyle} value={toToken?.symbol || ''} onChange={e => setToToken(tokens.find(t => t.symbol === e.target.value))}>
                {tokens.map(t => <option key={t.symbol} value={t.symbol}>{t.symbol}</option>)}
              </select>
            </div>
          </div>
          {isConnected && toToken && (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px', textAlign: 'right' }}>
              Available: {toBalance} {toToken.symbol}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Slippage:</span>
          {['0.1', '0.5', '1.0'].map(s => (
            <button key={s} onClick={() => setSlippage(s)} style={{ padding: '4px 10px', backgroundColor: slippage === s ? '#FB923C' : '#2a2a2a', color: slippage === s ? '#1A0800' : '#9ca3af', border: '1px solid #3a3a3a', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>{s}%</button>
          ))}
        </div>

        {(() => {
          // Check if this is wrap/unwrap operation
          const isWrapOp = (fromToken?.symbol === 'FLR' && toToken?.symbol === 'WFLR') ||
                          (fromToken?.symbol === 'WFLR' && toToken?.symbol === 'FLR');

          // Check if user has sufficient balance
          let hasInsufficientBalance = false;
          let userBalance = 0n;
          try {
            if (fromAmount && fromToken && isConnected) {
              const amountIn = parseUnits(fromAmount, fromToken.decimals ?? 18);
              if (fromToken.symbol === 'FLR') {
                userBalance = flrBalance?.value || 0n;
              } else if (fromTokenBalance !== undefined) {
                userBalance = fromTokenBalance;
              }
              hasInsufficientBalance = userBalance < amountIn;
            }
          } catch {}

          // Check if approval is needed (not needed for wrap operations)
          const needsApproval = !isWrapOp && fromToken?.symbol !== 'FLR' && fromAmount && fromToken && !notDeployed && isConnected;
          let amountInForApproval = null;
          try {
            if (needsApproval) {
              amountInForApproval = parseUnits(fromAmount, fromToken.decimals ?? 18);
            }
          } catch {}
          const allowance = tokenAllowance || 0n;
          const requiresApproval = needsApproval && amountInForApproval && allowance < amountInForApproval;

          if (requiresApproval) {
            return (
              <button className="button" onClick={handleApprove} disabled={isApprovePending || isApprovalConfirming || hasInsufficientBalance}>
                {isApprovePending || isApprovalConfirming ? 'Approving...' : hasInsufficientBalance ? 'Insufficient Balance' : 'Approve'}
              </button>
            );
          }

          // Determine button text based on operation type
          let buttonText = 'Swap';
          let processingText = 'Swapping...';

          if (isWrapOp) {
            if (fromToken?.symbol === 'FLR') {
              buttonText = 'Wrap';
              processingText = 'Wrapping...';
            } else {
              buttonText = 'Unwrap';
              processingText = 'Unwrapping...';
            }
          }

          return (
            <button className="button" onClick={handleSwap} disabled={!isConnected || !fromAmount || isPending || isConfirming || notDeployed || hasInsufficientBalance}>
              {isPending || isConfirming ? processingText : !isConnected ? 'Connect Wallet' : hasInsufficientBalance ? 'Insufficient Balance' : buttonText}
            </button>
          );
        })()}

        {status && (() => {
          const isOk  = status.includes('✅');
          const isErr = status.includes('❌') || status.includes('⚠️');
          const [headline, txLine] = status.split('\n');
          const isExplorerLine = txLine?.startsWith(EXPLORER);
          return (
            <div style={{
              marginTop: '16px',
              padding: '14px',
              backgroundColor: isOk ? '#0f3a1a' : isErr ? '#3a0f0f' : '#1a1a1a',
              border: `1px solid ${isOk ? '#4ade80' : isErr ? '#ef4444' : '#3a3a3a'}`,
              borderRadius: '8px',
              color: isOk ? '#4ade80' : isErr ? '#ef4444' : '#fff',
              fontSize: '0.95rem',
              fontWeight: '600',
              textAlign: 'center',
            }}>
              <div>{headline}</div>
              {txLine && isExplorerLine && (
                <div style={{ marginTop: '6px', fontSize: '0.78rem', fontWeight: '400' }}>
                  <a href={txLine} target="_blank" rel="noreferrer" style={{ color: '#A78BFA', wordBreak: 'break-all' }}>
                    View on explorer ↗
                  </a>
                </div>
              )}
              {txHash && !isExplorerLine && (
                <div style={{ marginTop: '6px', fontSize: '0.78rem', fontWeight: '400' }}>
                  <a href={`${EXPLORER}/tx/${txHash}`} target="_blank" rel="noreferrer" style={{ color: '#A78BFA', wordBreak: 'break-all' }}>
                    {txHash.slice(0, 10)}…{txHash.slice(-8)} ↗
                  </a>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default SwapSection;
